import { SdgAlignmentLevel, SdgTarget } from '@upright/common/model/output/scores/sdg-alignment/types';
import { TippyWithOriginalStyles } from '@upright/ui/common/generic/components/tooltips/default-tippy';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { getSdgTargetDescription } from '@upright/common/model/output/scores/sdg-alignment/sdg-targets';
import { ReasoningState } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/reasonings/sdg-reasoning-keys';
import { color } from 'd3-color';
import styled from 'styled-components';

interface Props {
  target: SdgTarget;
  level: SdgAlignmentLevel;
  // Generation state of the reasoning for this (product, target, direction):
  //   - 'ready'   -> show the generated sentence beneath the official description;
  //   - 'loading' -> show "Reasoning loading..." (generation still in progress);
  //   - 'failed' / undefined -> show only the official description, no extra text.
  reasoningState?: ReasoningState;
}

export function TargetBox(props: Props) {
  const backgroundColor = BouColor.TOOL_BACKGROUND_LIGHT_GRAY;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const borderColor = color(backgroundColor)!.darker(0.2).formatHex();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const textColor = color(backgroundColor)!.darker(4).formatHex();
  return (
    <Root>
      <TippyWithOriginalStyles
        interactive={true}
        trigger={'mouseenter focus'}
        placement={'bottom'}
        // Render on <body> and measure overflow against the viewport (not the
        // drilldown modal's scroll container) so a tall tooltip is never clipped
        // by a short modal — Popper flips it to whichever side has room. Without
        // this, in a short modal (few contributing products) the bubble overflows
        // the modal's bottom edge and the user has to scroll to read it (PLT-4963).
        // zIndex is bumped above the Primer Dialog so the body-appended tooltip
        // is not painted behind the modal.
        appendTo={() => document.body}
        zIndex={10000}
        popperOptions={{
          modifiers: [
            {
              name: 'flip',
              options: { fallbackPlacements: ['top', 'bottom'], boundary: document.body, rootBoundary: 'viewport' },
            },
            {
              name: 'preventOverflow',
              options: { boundary: document.body, rootBoundary: 'viewport', altAxis: true },
            },
          ],
        }}
        content={
          <TooltipContent>
            <b>Official description of target {props.target}</b>: {getSdgTargetDescription(props.target)}.<br />
            <Source>Source: UN</Source>.
            {props.reasoningState?.status === 'ready' && props.reasoningState.reasoning !== undefined && (
              <Reasoning>{props.reasoningState.reasoning}</Reasoning>
            )}
            {props.reasoningState?.status === 'loading' && (
              <Reasoning>
                <LoadingText>Reasoning loading...</LoadingText>
              </Reasoning>
            )}
          </TooltipContent>
        }
      >
        <span>
          <Main style={{ backgroundColor, color: textColor, borderColor }}>{props.target}</Main>
        </span>
      </TippyWithOriginalStyles>
    </Root>
  );
}

const TooltipContent = styled.div`
  font-weight: normal;
  text-align: left;
`;

const Source = styled.span`
  color: ${BouColor.TOOL_TEXT_GRAY};
`;

// Placeholder shown while the reasoning is still being generated.
const LoadingText = styled.span`
  font-style: italic;
  color: ${BouColor.TOOL_TEXT_GRAY};
`;

// Generated reasoning sits directly below the official description, no separator.
const Reasoning = styled.div`
  margin-top: 0.5em;
`;

const Root = styled.div``;

const Main = styled.div`
  cursor: default;
  white-space: nowrap;
  background-color: ${BouColor.TOOL_BACKGROUND_LIGHT_PURPLE};
  border-radius: 4px;
  border: 1px solid black;
  padding-left: 0.4em;
  padding-right: 0.4em;
  padding-top: 0.2em;
  padding-bottom: 0.2em;
  font-weight: 500;
`;
